/**
 * SUN + SHADOW STUDY — roadmap item #2 (Brockmann-approved 2026-06-23).
 *
 * "Cesium native sun position + shadows at a set date/time. Venue shadows AT
 *  event time = concealment, glare, sniper-hide, photographer angles."
 *
 * ================== WHY THIS IS TWO SWITCHES, NOT ONE ==================
 * SUN GEOMETRY (safe) — solar azimuth/elevation for the viewport centre at a
 *   chosen time, drawn as a sun-ray line and a cast-shadow line, plus a
 *   numeric readout. Pure maths and two polylines. No new shader variants.
 *
 * SHADOW MAPS (risky, separate switch) — real shadows cast by the 3D
 *   buildings. This compiles NEW shader variants, and this exact engine
 *   already killed the whole render context twice on this iOS/WebKit build:
 *   b598 and b600 both chased `_uczm_computeGroundAtmosphere` failing MSL
 *   compilation via ANGLE, which is why CesiumProvider ships with
 *   skyAtmosphere, groundAtmosphere, fog and tileset shadows all forced OFF.
 *   If a shadow-map compile fails the same way, it takes the PAGE down, not
 *   just this layer — so it is opt-in, default OFF, reversible, and a page
 *   reload recovers.
 *
 * Splitting them means the operator still gets the actionable advance-work
 * answer — what is the sun angle at event time — even if shadow maps turn
 * out to be unusable on the phone.
 * =======================================================================
 *
 * DEFAULT OFF (both).
 */

import { createEffect, onCleanup } from "solid-js";
import { useCesium } from "../../cesium/useCesium.ts";
import { groundState, setSunMeta } from "./store.ts";
import { sunPosition, compassPoint, shadowBearing } from "../../utils/solar.ts";
import { aimPointDegrees } from "../../utils/viewRect.ts";
/** Length of the drawn sun-ray / shadow lines, metres. */
const RAY_LEN_M = 900;

/** Reference object height for the reported shadow-length ratio, metres. */
const REF_HEIGHT_M = 10;
const EARTH_R = 6_371_000;

/** Forward geodesic: move from (lat,lon) along a bearing for a distance. */
function destination(latDeg, lonDeg, bearingDeg, distanceM) {
  const d = distanceM / EARTH_R;
  const br = bearingDeg * Math.PI / 180;
  const lat1 = latDeg * Math.PI / 180;
  const lon1 = lonDeg * Math.PI / 180;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
  const lon2 = lon1 + Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return {
    lat: lat2 * 180 / Math.PI,
    lon: (lon2 * 180 / Math.PI + 540) % 360 - 180
  };
}

/** The instant being studied: now, plus the operator's scrub offset. */
function studyTime() {
  return new Date(Date.now() + groundState.sunTimeOffsetMin * 60_000);
}
export function SunShadowLayer() {
  const {
    viewer,
    ready
  } = useCesium();
  let sunRay = null;
  let shadowLine = null;
  let moveEndHandler = null;
  /** Remembers what we changed so disable restores the shipped defaults. */
  let shadowsApplied = false;
  const clearEntities = () => {
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    // 2026-08-31 BLINK FIX (Brockmann: "blinking line"). update() removed both
    // polylines and re-added them, so for one frame they were simply gone —
    // that flicker is the blink. suspendEvents() batches the remove and the
    // re-add into a SINGLE collection update, so they never disappear.
    //
    // Resume is queued as a microtask rather than paired with an explicit
    // resume call because update() is SYNCHRONOUS: everything from here to the
    // end of update() — including both entities.add() calls — runs before the
    // microtask fires, so the re-adds land inside the same suspend window.
    // It also cannot be skipped by update()'s early return when the sun is
    // below the horizon, which a naive suspend/resume pair WOULD skip, leaving
    // the collection suspended forever and freezing the layer.
    v.entities.suspendEvents();
    queueMicrotask(() => {
      try {
        if (!v.isDestroyed()) v.entities.resumeEvents();
      } catch (_) {/* torn down */}
    });
    // (viewer already resolved above)
    // (destroyed-guard already applied above)
    for (const e of [sunRay, shadowLine]) {
      if (e) {
        try {
          v.entities.remove(e);
        } catch (_) {/* already gone */}
      }
    }
    sunRay = null;
    shadowLine = null;
  };

  /**
   * The ground point being looked at.
   * Uses a centre-screen pickEllipsoid, NOT camera.computeViewRectangle() —
   * on this hidden-globe scene that returns the whole globe, so the centre
   * came out as (0,0) and the sun read "below horizon" over daylit Houston.
   * Measured and fixed 2026-08-17.
   */
  const viewCentre = () => {
    const v = viewer();
    if (!v || v.isDestroyed()) return null;
    return aimPointDegrees(v);
  };

  /** Recompute sun geometry and redraw the two indicator lines. */
  const update = () => {
    const v = viewer();
    if (!v || v.isDestroyed() || !groundState.sunStudyEnabled) return;
    const c = viewCentre();
    if (!c) return;
    const when = studyTime();
    const {
      azimuth,
      elevation
    } = sunPosition(when, c.lat, c.lon);
    const shadowAz = shadowBearing(azimuth);
    const up = elevation > 0;

    // Shadow length for a REF_HEIGHT_M object. Below ~2 deg the value explodes
    // toward infinity and stops meaning anything operationally.
    const ratio = elevation > 2 ? REF_HEIGHT_M / Math.tan(elevation * Math.PI / 180) : Infinity;
    setSunMeta({
      azimuth,
      elevation,
      shadowBearing: shadowAz,
      compass: compassPoint(azimuth),
      shadowLengthM: Number.isFinite(ratio) ? ratio : null,
      isUp: up,
      timeISO: when.toISOString()
    });
    clearEntities();
    if (!up) {
      // Sun below the horizon: no ray, no shadow. Readout still says so.
      v.scene.requestRender();
      return;
    }

    // --- sun ray: from the ground point out toward the sun ---
    const rayEnd = destination(c.lat, c.lon, azimuth, RAY_LEN_M);
    const rayHeight = RAY_LEN_M * Math.tan(elevation * Math.PI / 180);
    sunRay = v.entities.add({
      name: "SUN RAY",
      polyline: {
        positions: [Cesium.Cartesian3.fromDegrees(c.lon, c.lat, 2), Cesium.Cartesian3.fromDegrees(rayEnd.lon, rayEnd.lat, rayHeight)],
        width: 4,
        arcType: Cesium.ArcType.NONE,
        material: Cesium.Color.fromCssColorString("#ffd24d"),
        // Draw over the 3D tiles rather than being buried inside a building.
        depthFailMaterial: Cesium.Color.fromCssColorString("#ffd24d").withAlpha(0.55)
      }
    });

    // --- cast shadow: on the ground, 180 deg opposite the sun ---
    const shadowLen = Number.isFinite(ratio) ? Math.max(60, Math.min(RAY_LEN_M, ratio * 12)) : RAY_LEN_M;
    const shEnd = destination(c.lat, c.lon, shadowAz, shadowLen);
    shadowLine = v.entities.add({
      name: "CAST SHADOW",
      polyline: {
        positions: [Cesium.Cartesian3.fromDegrees(c.lon, c.lat, 2), Cesium.Cartesian3.fromDegrees(shEnd.lon, shEnd.lat, 2)],
        width: 6,
        arcType: Cesium.ArcType.NONE,
        material: Cesium.Color.fromCssColorString("#2b3f55"),
        depthFailMaterial: Cesium.Color.fromCssColorString("#2b3f55").withAlpha(0.6)
      }
    });
    console.log(`[SunShadow] ${when.toISOString()} @ ${c.lat.toFixed(3)},${c.lon.toFixed(3)} ` + `az=${azimuth.toFixed(1)} el=${elevation.toFixed(1)} entities=${v.entities.values.length}`);
    v.scene.requestRender();
  };

  /**
   * Shadow maps. Isolated, guarded, and fully reversible — see the header
   * for why this is quarantined from the sun-geometry half.
   */
  const applyShadows = enable => {
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    const ts = window.__tileset;
    try {
      if (enable) {
        // Drive Cesium's own sun from the studied instant so the shadow map
        // is cast for event time, not wall-clock time.
        v.clock.shouldAnimate = false;
        v.clock.currentTime = Cesium.JulianDate.fromDate(studyTime());
        v.shadows = true;
        v.shadowMap.enabled = true;
        v.shadowMap.softShadows = true;
        v.shadowMap.size = 2048;
        v.shadowMap.maximumDistance = 8_000;
        if (ts) ts.shadows = Cesium.ShadowMode.ENABLED;
        shadowsApplied = true;
        console.log("[SunShadow] shadow maps ENABLED (shader-compile risk path)");
      } else if (shadowsApplied) {
        v.shadows = false;
        if (v.shadowMap) v.shadowMap.enabled = false;
        // Restore the shipped default set in CesiumProvider.
        if (ts) ts.shadows = Cesium.ShadowMode.DISABLED;
        shadowsApplied = false;
        console.log("[SunShadow] shadow maps disabled, tileset restored to DISABLED");
      }
      v.scene.requestRender();
    } catch (e) {
      console.error("[SunShadow] shadow map toggle failed:", e);
      setSunMeta({
        shadowError: String(e)
      });
    }
  };

  // --- sun geometry: attach/detach with the safe toggle ---
  createEffect(() => {
    const on = groundState.sunStudyEnabled;
    // Track the scrub so moving the time slider redraws immediately.
    void groundState.sunTimeOffsetMin;
    if (!ready()) return;
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    if (moveEndHandler) {
      v.camera.moveEnd.removeEventListener(moveEndHandler);
      moveEndHandler = null;
    }
    if (on) {
      moveEndHandler = update;
      v.camera.moveEnd.addEventListener(moveEndHandler);
      update();
    } else {
      clearEntities();
      setSunMeta({
        isUp: false,
        timeISO: null
      });
      v.scene.requestRender();
    }
  });

  // --- shadow maps: independent toggle, and only while sun study is on ---
  createEffect(() => {
    const want = groundState.sunShadowsEnabled && groundState.sunStudyEnabled;
    void groundState.sunTimeOffsetMin; // re-cast when the studied time moves
    if (!ready()) return;
    applyShadows(want);
  });
  onCleanup(() => {
    const v = viewer();
    if (v && !v.isDestroyed() && moveEndHandler) {
      v.camera.moveEnd.removeEventListener(moveEndHandler);
    }
    applyShadows(false);
    clearEntities();
  });
  return null;
}
export default SunShadowLayer;